<template>
  <div class="experience-modal">
    <v-dialog
      v-model="isVisible"
      max-width="566"
      transition="dialog-transition"
    >
      <v-card>
        <v-card-title
          class="experience-modal__title headline"
          primary-title
        >
          EXPERIENCE
          <v-btn icon large color="primary" @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pb-0">
          <v-form ref="experienceForm" v-model="isFormValid" @submit.prevent="submitForm">
            <v-row class="experience-modal__row">
              <v-col class="experience-modal__col">
                <v-text-field
                  v-model="experience.project_name"
                  class="form-field"
                  :rules="[rules.required]"
                  dense
                  label="Project name*"
                  placeholder="Project name"
                />
              </v-col>
            </v-row>

            <v-row class="experience-modal__row">
              <v-col class="experience-modal__col">
                <v-menu
                  ref="startDate"
                  content-class="datepicker-menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template #activator="{ on }">
                    <div class="form-field--date-wrapper" v-on="on">
                      <v-text-field
                        :value="experience.start_date"
                        class="form-field"
                        clearable
                        dense
                        label="Start date"
                        placeholder="Chose date"
                        readonly
                        v-on="on"
                        @click:clear="experience.start_date = ''"
                      >
                        <template #append>
                          <v-icon color="blue">
                            mdi-calendar
                          </v-icon>
                        </template>
                      </v-text-field>
                    </div>
                  </template>
                  <v-date-picker
                    v-model="experience.start_date"
                    :max="experience.end_date"
                    no-title
                    type="month"
                    scrollable
                    @input="$refs.startDate.save(true)"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col class="experience-modal__col">
                <v-menu
                  ref="endDate"
                  content-class="datepicker-menu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                >
                  <template #activator="{ on }">
                    <div class="form-field--date-wrapper" v-on="on">
                      <v-text-field
                        :value="localEndDate"
                        class="form-field"
                        clearable
                        dense
                        label="End date"
                        placeholder="Chose date"
                        readonly
                        v-on="on"
                        @click:clear="localEndDate = ''"
                      >
                        <template #append>
                          <v-icon color="blue">
                            mdi-calendar
                          </v-icon>
                        </template>
                      </v-text-field>
                    </div>
                  </template>
                  <v-date-picker
                    v-model="localEndDate"
                    type="month"
                    no-title
                    scrollable
                    :min="experience.start_date"
                    @input="$refs.endDate.save(true)"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
            <v-row class="experience-modal__row">
              <v-col class="experience-modal__col">
                <div class="form-field--date-wrapperv experience-modal__about">
                  <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
                    <div class="menubar">
                      <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.bullet_list() }"
                        @click.prevent="commands.bullet_list"
                      >
                        <v-icon color="blue">
                          mdi-format-list-bulleted-square
                        </v-icon>
                      </button>

                      <button
                        class="menubar__button"
                        :class="{ 'is-active': isActive.bold() }"
                        @click.prevent="commands.bold"
                      >
                          <v-icon color="blue">
                            mdi-format-bold
                          </v-icon>
                      </button>
                    </div>
                  </editor-menu-bar>
                  <editor-content :editor="editor"/>
                </div>
                <p
                  class="experience-modal__validation"
                  :class="{'show' : !wysiwygValidation}"
                >
                  Required.
                </p>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="experience-modal__actions">
          <v-row class="experience-modal__row">
            <v-col class="experience-modal__col"></v-col>
            <v-col class="experience-modal__col text-right">
              <v-btn
                color="blue"
                width="100%"
                large
                dark
                :loading="isSubmitProcessing"
                @click="submit"
              >
               {{ selectedExperience.context === 'add' ? 'Add experience' : 'Save' }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { Editor, EditorContent, EditorMenuBar } from 'tiptap';
import {
  ListItem,
  Bold,
  BulletList,
} from 'tiptap-extensions';
import validationRules from '@/helpers/validators';

const rules = validationRules;

export default {
  components: {
    EditorContent,
    EditorMenuBar,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    selectedExperience: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      rules,
      isFormValid: true,
      experience: {},
      wysiwygContent: '',
      wysiwygValidation: true,
      localEndDate: '',
      editor: new Editor({
        extensions: [
          new ListItem(),
          new Bold(),
          new BulletList(),
        ],
        onUpdate: ({ getHTML }) => {
          const newContent = getHTML();
          this.experience.about = newContent;
        },
        onBlur: this.validateWysiwyg,
      }),
    };
  },

  computed: {
    ...mapState('hrDeveloper', [
      'developer',
      'isSubmitProcessing',
    ]),
    ...mapState('developer', { dev: 'developer' }),

    ...mapState('features', [
      'featuresData',
    ]),

    isRegistration() {
      return this.$route.name === 'developer-join';
    },

    isVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },

  watch: {
    isVisible() {
      if (this.$refs.experienceForm && this.isVisible) {
        this.$refs.experienceForm.reset();
      }
      this.wysiwygValidation = true;
      this.experience = this.isVisible ? { ...this.selectedExperience } : {};
      this.localEndDate = this.experience.end_date === 'ongoing' ? '' : this.experience.end_date;
    },

    experience() {
      if (this.experience.about) {
        this.editor.setContent(this.experience.about);
      }
    },
  },

  beforeDestroy() {
    this.editor.destroy();
  },

  methods: {
    ...mapActions('hrDeveloper', [
      'updateDeveloper',
    ]),

    ...mapActions('developer', [
      'addDeveloperData',
    ]),

    closeModal() {
      this.$emit('input', false);
      this.editor.setContent('');
    },
    validateWysiwyg() {
      this.wysiwygValidation = Boolean(this.experience.about) && this.experience.about !== '<p></p>';
    },
    submit() {
      this.$refs.experienceForm.validate();
      this.validateWysiwyg();

      if (this.isFormValid && this.wysiwygValidation) {
        const newData = {};
        newData.experience = this.isRegistration
          ? [...this.dev.experience] : [...this.developer.experience];
        this.experience.about = this.experience.about.replace('<li><p></p></li>', '');
        if (this.selectedExperience.context === 'add') {
          this.experience.end_date = !this.localEndDate ? 'ongoing' : this.localEndDate;
          newData.experience.push(this.experience);
        } else {
          newData.experience[this.selectedExperience.index] = this.experience;
          newData.experience[this.selectedExperience.index].end_date = !this.localEndDate ? 'ongoing' : this.localEndDate;
        }

        newData.experience.sort((a, b) => {
          if (a.start_date > b.start_date) return -1;
          if (a.start_date < b.start_date) return 1;
          return 0;
        });

        if (this.isRegistration) {
          this.addDeveloperData(newData)
            .then(() => {
              this.closeModal();
            });
        } else {
          this.updateDeveloper(newData)
            .then(() => {
              this.closeModal();
            });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.experience-modal {
  &__title {
    display: flex;
    justify-content: space-between;
    font-weight: 600 !important;
    font-size: 22px !important;
  }

  &__actions {
    padding: 16px 24px 16px 24px;
    justify-content: flex-end;
  }

  &__row {
    margin-right: -8px;
    margin-left: -8px;
  }

  &__col {
    padding: 0 8px 8px;
  }

  &__subtitle {
    color: $color--primary;
    font-size: 18px;
    line-height: 21px;
    font-weight: 400;
    margin-bottom: 8px;
  }

  &__about {
    border: 1px solid #E2E6EA;
    border-radius: 4px;
    padding-top: 10px;
    padding-bottom: 0px;
    padding-left: 16px;
    padding-right: 16px;
    font-size: 14px;
    min-height: 154px;
    height: auto;
    background-color: white;
    position: relative;
    z-index: 2;
  }
  &__validation {
    font-size: 12px;
    color: #ff5252;
    transform: translateY(-20px);
    transition: all 0.2s ease;
    &.show {
      transform: translateY(0);
    }
  }
}
</style>
